import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCirclePlus } from 'lucide-react';

const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();
    if (isToday) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (isYesterday) return 'Gestern';
    return date.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
};

const ConversationList = ({ conversations, activeConversationId, onSelect, loading }) => {
    const navigate = useNavigate();

    if (loading && conversations.length === 0) {
        return (
            <div className="space-y-0.5 p-2">
                {[1, 2, 3, 4].map(i => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-2xl animate-pulse">
                        <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-white/5 flex-shrink-0" />
                        <div className="flex-1 space-y-2">
                            <div className="h-3.5 bg-gray-200 dark:bg-white/5 rounded-full w-1/3" />
                            <div className="h-3 bg-gray-100 dark:bg-white/[0.03] rounded-full w-2/3" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (conversations.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center p-8 text-center min-h-[280px]">
                <div className="w-16 h-16 mb-4 rounded-[1.5rem] bg-plantlix-50 dark:bg-plantlix-900/20 flex items-center justify-center">
                    <MessageCirclePlus className="w-8 h-8 text-plantlix-500" strokeWidth={1.5} />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">Noch keine Chats</h3>
                <p className="text-sm text-gray-400 dark:text-gray-500 mb-5 leading-relaxed max-w-[200px]">
                    Finde Freunde und starte deine erste Konversation!
                </p>
                <button
                    onClick={() => navigate('/friends?tab=add')}
                    className="px-5 py-2.5 bg-plantlix-500 hover:bg-plantlix-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-plantlix-500/25 transition-all active:scale-95"
                >
                    Freunde finden
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-0.5 p-2 pb-4">
            {conversations.map(conv => {
                const isActive = activeConversationId === conv.id;
                const hasUnread = conv.unread_count > 0;
                const lastMsg = conv.last_message;
                return (
                    <button
                        key={conv.id}
                        onClick={() => onSelect(conv)}
                        className={`w-full flex items-center gap-3 p-3 rounded-2xl text-left transition-all duration-150 group ${
                            isActive ? 'bg-plantlix-50 dark:bg-plantlix-900/20' : 'hover:bg-gray-50 dark:hover:bg-white/[0.04]'
                        }`}
                    >
                        <div className="relative flex-shrink-0">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-black text-lg shadow-sm ${
                                isActive ? 'bg-gradient-to-br from-plantlix-500 to-plantlix-700' : 'bg-gradient-to-br from-slate-500 to-slate-700'
                            }`}>
                                {conv.partner.username.charAt(0).toUpperCase()}
                            </div>
                            {conv.partner.is_online && (
                                <span className="absolute bottom-0 right-0 block w-3 h-3 rounded-full bg-plantlix-500 border-2 border-white dark:border-[#141414]" />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-baseline mb-0.5">
                                <span className={`font-bold text-sm truncate ${hasUnread ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-200'}`}>
                                    {conv.partner.username}
                                </span>
                                {lastMsg && (
                                    <span className={`text-[11px] flex-shrink-0 ml-2 ${hasUnread ? 'text-plantlix-600 dark:text-plantlix-400 font-bold' : 'text-gray-400'}`}>
                                        {formatTime(lastMsg.timestamp)}
                                    </span>
                                )}
                            </div>
                            <div className="flex justify-between items-center gap-2">
                                <p className={`text-xs truncate ${hasUnread ? 'text-gray-700 dark:text-gray-300 font-semibold' : 'text-gray-400 dark:text-gray-500'}`}>
                                    {lastMsg
                                        ? (lastMsg.sender !== conv.partner.id ? 'Du: ' : '') + lastMsg.content
                                        : <span className="italic">Noch keine Nachrichten</span>}
                                </p>
                                {hasUnread && (
                                    <span className="flex-shrink-0 bg-plantlix-500 text-white text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center">
                                        {conv.unread_count > 9 ? '9+' : conv.unread_count}
                                    </span>
                                )}
                            </div>
                        </div>
                    </button>
                );
            })}
        </div>
    );
};

export default ConversationList;
